import React, { useMemo } from 'react'
import { useRecoilValue } from 'recoil'
import styled from '@emotion/styled'
import { Colors, Shapes } from 'src/components/theme'
import { InsightIcon } from 'src/components/sidebar-panels/available-insights/insight-icon'
import { LoadingInsightsPanel, Panel } from 'src/components/sidebar/panel'
import { PropertyRecords } from 'src/components/insights/property-records'
import { Zoning } from 'src/components/insights/zoning'
import { Demographics } from 'src/components/insights/demographics'
import { CarTraffic } from 'src/components/insights/car-traffic'
import { RadiusRingsTradeArea } from 'src/components/insights/radius-rings-trade-area'
import { DriveTimeTradeArea } from 'src/components/insights/drive-time-trade-area'
import { AreaVisits } from 'src/components/insights/area-visits'
import { CustomPolygonTTA } from 'src/components/insights/custom-polygon-tta'
import { CustomPolygonVisits } from 'src/components/insights/custom-polygon-visits'
import { Mobility } from 'src/components/insights/location-visits'
import { Insight } from 'src/components/insights/types'
import { TrueTradeArea } from 'src/components/insights/true-trade-area'
import { Rank } from 'src/components/insights/rank'
import { CategoryRank } from 'src/components/insights/category-rank'
import { TenantOverview } from 'src/components/insights/tenant-overview'
import { OwnerSearchLegacy } from 'src/components/insights/owner-search-legacy'
import { SearchResultType } from 'src/atoms/search'
import { availableInsights } from 'src/components/insights/atoms'
import { usePreventUserAccess } from 'src/hooks/use-prevent-user-access'
import { PlaceDebug } from 'src/components/insights/place-debug'
import { IS_PROD } from 'src/config'
import { OwnerSearch } from 'src/components/insights/owner-search'
import { BrandProfile } from 'src/components/insights/brand-profile'
import { CustomPolygonCategoryRank } from 'src/components/insights/custom-polygon-category-rank'
import { NoBrandRankInsight } from 'src/components/insights/performance-benchmarks/no-rank'

const InsightsWrapper = styled.div<{ minHeight: number }>`
  min-height: ${(props) => props.minHeight}px;
  padding-bottom: ${Shapes.spacing[2]}px;
  display: flex;
  flex-direction: column;
  gap: ${Shapes.spacing[4]}px;
`

const LOADING_ROWS = 7

const PARCEL_INSIGHTS = [PropertyRecords.id, Zoning.id, OwnerSearch.id, OwnerSearchLegacy.id]

const AREA_INSIGHTS = [RadiusRingsTradeArea.id, DriveTimeTradeArea.id, Demographics.id, CarTraffic.id]

const MOBILITY_INSIGHTS = [
  TrueTradeArea.id,
  CustomPolygonTTA.id,
  Mobility.id,
  CustomPolygonVisits.id,
  AreaVisits.id,
  CustomPolygonCategoryRank.id,
  Rank.id,
  NoBrandRankInsight.id,
  CategoryRank.id,
  TenantOverview.id,
  BrandProfile.id,
]

if (!IS_PROD) {
  MOBILITY_INSIGHTS.push(PlaceDebug.id)
}

type Props = {
  only?: SearchResultType
}

export const AvailableInsightsContent: React.FC<Props> = React.memo(({ only }) => {
  const { preventAccess } = usePreventUserAccess()
  const insights = useRecoilValue(availableInsights)
    .filter((x) => !x.hideOnSidebar)
    .filter((x) => !only || x.supportedResultTypes.includes(only))

  const insightsMap = useMemo(() => {
    const map = new Map<string, Insight>()
    insights.forEach((x) => map.set(x.id, x))
    return map
  }, [insights])

  // give the div a height so it pushes things which lazy load down farther
  const minHeight = 100 + insights.length * 30

  const PARCEL_ITEMS = PARCEL_INSIGHTS.map((x) => {
    const insight = insightsMap.get(x)
    if (!insight) {
      return null
    }
    return <InsightIcon insight={insight} key={x} color={Colors.orangeDark} />
  })

  const AREA_ITEMS = AREA_INSIGHTS.map((x) => {
    const insight = insightsMap.get(x)
    if (!insight) {
      return null
    }
    return <InsightIcon insight={insight} key={x} color={Colors.blueDark} />
  })

  const MOBILITY_ITEMS = MOBILITY_INSIGHTS.map((x) => {
    const insight = insightsMap.get(x)
    if (!insight) {
      return null
    }
    const finalInsight = { ...insight }
    finalInsight.name = finalInsight.name.replace('(Mobile Data)', '')
    return <InsightIcon color={Colors.purpleDark} insight={finalInsight} key={x} />
  })

  // if totaly empty return null
  if (!PARCEL_ITEMS.some((x) => x) && !AREA_ITEMS.some((x) => x) && !MOBILITY_ITEMS.some((x) => x)) {
    return null
  }

  return (
    <InsightsWrapper minHeight={minHeight}>
      {PARCEL_ITEMS.some((x) => x) && (
        <>
          <Panel title="Parcel Reports" noPadding>
            {PARCEL_ITEMS}
          </Panel>
        </>
      )}
      {AREA_ITEMS.some((x) => x) && (
        <>
          <Panel title="Area Reports" noPadding>
            {AREA_ITEMS}
          </Panel>
        </>
      )}
      {/* Prevent access to black listed user */}
      {!preventAccess && (
        <>
          {MOBILITY_ITEMS.some((x) => x) && (
            <Panel title="Mobility Reports" noPadding>
              {MOBILITY_ITEMS}
            </Panel>
          )}
        </>
      )}
    </InsightsWrapper>
  )
})

export const AvailableInsights: React.FC<Props> = React.memo(({ only }) => {
  const fallback = <LoadingInsightsPanel title="Reports" rows={LOADING_ROWS} />
  return (
    <>
      <React.Suspense fallback={fallback}>
        <AvailableInsightsContent only={only} />
      </React.Suspense>
    </>
  )
})
